/* eslint-disable max-len */
import React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import SplitPane from 'react-split-pane';
import useLocalStorage from 'react-use/lib/useLocalStorage';
import { Redirect } from 'wouter';
import { cieq, useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { Fill } from '../../ui/Fill';
import { VerticalTabSet } from '../../ui/VerticalTabSet';
import { UseLocationScope } from '../AppLocation';
import { useResolve } from '../Container';
import { AccountHeaderBlock } from '../account/AccountHeaderBlock';
import { PlanStateHeaderBlock } from '../account/PlanStateHeaderBlock';
import { Crumbs } from '../common/Crumbs';
import { EnableIfHasFeature, FeatureCallout, HideIfHasFeature } from '../common/FeatureElements';
import { Page } from '../common/Page';
import { Copy, FlexFrame, Spacer, Title } from '../dashboard/elements/LayoutElements';
import { UpgradeButton } from '../upgrade/UpgradeButton';
import { remInPx } from './../../ui';
import { ManageWorkspacesCreateView } from './ManageWorkspacesCreateView';
import { ManageWorkspacesDetailView } from './ManageWorkspacesDetailView';
import { ManageWorkspacesModel, ManageWorkspacesScope } from './ManageWorkspacesScope';

const XScrollbars = Scrollbars as any;
const XSplitPane = SplitPane as any;

const ManageWorkspacesLayout: LFC<{ param?: string }> = ({ param }) => {

    const [leftSize, setLeftSize] = useLocalStorage('deqo/ui/people/leftSize', remInPx(20));
    const workspaces = useObserver(useResolve(ManageWorkspacesModel));
    const selected = workspaces.find(x => cieq(x.id, param));

    if (!param || (param !== 'create' && !selected)) {
        return <Redirect to={`~/manage/workspaces/${workspaces[0]?.id || 'create'}`} />;
    }

    return <Fill flex="column">
        <FlexFrame>
            <VerticalTabSet>
                <XSplitPane className="flex" split="vertical" minSize={remInPx(12)} size={leftSize} maxSize={remInPx(30)} onChange={setLeftSize}>
                    <VerticalTabSet.Menu expand>
                        <VerticalTabSet.Link to="~/manage/workspaces/create" selected={param === 'create'}>
                            <Icon size="1" src="deqo://icons/hero/plus" />&nbsp;Create Workspace
                        </VerticalTabSet.Link>
                        <Spacer />
                        <XScrollbars autoHide>
                            {workspaces.map(x =>
                                <VerticalTabSet.Link key={x.id} to={`~/manage/workspaces/${x.id}`} selected={x === selected}>
                                    {x.label}
                                </VerticalTabSet.Link>
                            )}
                        </XScrollbars>
                    </VerticalTabSet.Menu>
                    <VerticalTabSet.Content>
                        <HideIfHasFeature name="workspaces">
                            <FeatureCallout maxWidth="64" margin="2 2 1 2">
                                <Title>This feature is only available on the Pro &amp; Business plans.</Title>
                                <Copy>
                                    In order to create and manage Workspaces, your organization must be a subscriber to our Pro or Business plans.
                                    Workspaces allow you to better organize your boards as well as provide a level of access control within your team space.
                                </Copy>
                                <UpgradeButton variant="common" />
                            </FeatureCallout>
                        </HideIfHasFeature>
                        <EnableIfHasFeature name="workspaces">
                            {selected && <>
                                <ManageWorkspacesDetailView key={selected.id} workspace={selected} />
                            </>}
                            {param === 'create' && <>
                                <ManageWorkspacesCreateView />
                            </>}
                        </EnableIfHasFeature>
                    </VerticalTabSet.Content>
                </XSplitPane>
            </VerticalTabSet>
        </FlexFrame>
    </Fill>;
};

export const ManageWorkspacesView: LFC<{ param?: string }> = ({ param }) =>
    <ManageWorkspacesScope>
        <UseLocationScope name="workspaces" label="Workspaces" />
        <Page.Header>
            <Crumbs items={[
                ['Manage', '/manage'],
                ['Workspaces', null],
            ]} />
            <Page.HeaderPush>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page.HeaderPush>
        </Page.Header>
        <Page.Content>
            <ManageWorkspacesLayout param={param} />
        </Page.Content>
    </ManageWorkspacesScope>;

